import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AbstractService } from '../commons/abstract.service';
import { SupportedLocale } from '../i18n/supported-locale';
import { Company, CompanyCrmLists } from '../models/company.model';

@Injectable({ providedIn: 'root' })
export class CompanyService extends AbstractService<Company> {

  getURLBase(): string {
    return '/company';
  }

  /**
   * Lead-quality configuration in force for the caller's company (RF-33, RF-34).
   * `isDefault` distinguishes "the company configured this" from "the company follows
   * the platform default", which the settings screen must state (AC-18).
   */
  getLeadQualityConfig(): Observable<{ config: LeadQualityConfig; isDefault: boolean }> {
    return super.get('/lead-quality');
  }

  /** Replaces the configuration and enqueues a recompute of the open deals (RF-34). */
  updateLeadQualityConfig(config: LeadQualityConfig): Observable<LeadQualityConfig> {
    return super.put('/lead-quality', config);
  }

  /** The segment and company-size lists in force — the platform default when the company set none (E2 RF-09). */
  getCrmLists(): Observable<CompanyCrmLists> {
    return super.get('/crm-lists');
  }

  /** Admin-only on the API, like the lead-quality criteria. */
  updateCrmLists(lists: CompanyCrmLists): Observable<CompanyCrmLists> {
    return super.put('/crm-lists', lists);
  }

  /**
   * The query the Trend Gestão improvements ranking is opened with — the ranking's own parameter
   * names, signed by the API when the caller may vote for the company.
   */
  getImprovementRankingLink(): Observable<Record<string, string>> {
    return super.get('/improvement-ranking-link');
  }

  constructor(private http: HttpClient) {
    super(http);
  }

  preCreate(obj: Company): void {
    delete obj?._id;
  }
  preUpdate(obj: Company): void {
  }

  getCountsForPanelAdm(queryParams: any = {}) {
    return super.getWithParams('/count/adm', queryParams);
  }

  getSubscriptionDetails(idCompany: string) {
    return super.get(`/subscription/details/${idCompany}`);
  }

  // Set the Company default operating language (admin-only, RF-01/RF-12).
  // Hits PUT /api/company/:id/language (T-15), which enforces the ADMIN role.
  setLanguage(idCompany: string, language: SupportedLocale): Observable<Company> {
    return super.put(`/${idCompany}/language`, { language });
  }
}


/** Lead-quality configuration (SPEC-2026-08-crm-pipeline-deals-radar — RF-33, RF-34). */
export interface LeadQualityCriterion {
  key: string;
  block?: string;
  weight: number;
  source?: 'DETERMINISTIC' | 'COPILOT';
}

export interface LeadQualityConfig {
  scoreCriteria: Array<LeadQualityCriterion>;
  penalties: Array<{ key: string; points: number }>;
  qualification: {
    mqlMin: number;
    sqlMin: number;
    sqlTriggers: Array<{ key: string; taskTypes: Array<string>; outcome: string }>;
  };
  healthCriteria: Array<LeadQualityCriterion>;
  /** Ranged bands only — NEW has no range (E3 RF-11). */
  healthBands: Array<{ band: string; min: number; max: number }>;
  stagnationDays: number;
  /** E3 blocks — optional: a config saved under E1 lacks them and the API falls back per field. */
  scoreBands?: { moderateMin: number; strongMin: number };
  cadenceMaxAttempts?: number;
  icp?: { segments: Array<string>; employeeBands: Array<string> };
}
